/*eslint-disable*/
import React, { useEffect, useRef, useState } from 'react';
import { Col, Form, Input, Row, Select } from 'antd';
import InputLabel from 'components/InputLabel/index';
import { NumericFormat } from 'react-number-format';
import { withValueCap } from 'pages/globals/global';
import TableContentModal from '../LeaseUtils/Modal/TableContentModal';
import CardAccordion from '../CardAccordion';
import GrossRent from './GrossRent';
import AdvanceAmount from './AdvanceAmount';
import { constantFormValues } from '../../root';

/*eslint-disable*/
const FinancialTermsConditions = ({ screen, dataFullyLoaded = false }) => {
	const form = Form.useFormInstance();
	const [showGrossRentModal, setShowGrossRentModal] = useState(false);
	const [showAdvanceAmountModal, setAdvanceAmountModal] = useState(false);
	const isUpdatingRef = useRef(false);
	const grossRentList = Form.useWatch('grossRentList', form);
	const advanceAmountList = Form.useWatch('advanceAmountList', form);
	const grossRentKey = useRef('');
	const advanceAmountKey = useRef('');

	useEffect(() => {
		if (!Array.isArray(grossRentList) || !Array.isArray(advanceAmountList) || grossRentList.length === 0 || advanceAmountList.length === 0)
			return;

		// Generate keys for comparison
		const newGrossRentKey = grossRentList.map((item) => `${item.from}-${item.to}`).join('|');
		const newAdvanceAmountKey = advanceAmountList.map((item) => `${item.from}-${item.to}`).join('|');

		// Skip if no changes in the structure
		if (newGrossRentKey === grossRentKey.current && newAdvanceAmountKey === advanceAmountKey.current) return;

		// Prevent updates if we're already in an update cycle
		if (isUpdatingRef.current) return;

		// Set flag to indicate we're updating
		isUpdatingRef.current = true;

		try {
			// Update ref values
			grossRentKey.current = newGrossRentKey;
			advanceAmountKey.current = newAdvanceAmountKey;
			const maxGrossRentTerm = Math.max(...grossRentList.map((item) => item.to ?? 0));
			const maxAdvanceAmountTerm = Math.max(...advanceAmountList.map((item) => item.to ?? 0));
			const maxTerm = Math.max(maxGrossRentTerm, maxAdvanceAmountTerm);

			// Check if the value actually needs to be updated
			const currentLeaseTerm = form.getFieldValue('leaseTerm');
			if (currentLeaseTerm !== maxTerm) {
				form.setFieldValue('leaseTerm', maxTerm);
				if (screen?.toLowerCase() === 'revise') {
					const currentChanges = form.getFieldValue('changes');
					if (currentChanges?.length > 0) {
						form.setFieldValue(['changes', currentChanges.length - 1, 'inputChangeLeaseTerm'], maxTerm);
					}
				}

				if (screen?.toLowerCase() === 'create') {
					const currentChangeLeaseTerm = form.getFieldValue(['changes', 0, 'inputChangeLeaseTerm']);
					if (currentChangeLeaseTerm !== maxTerm) {
						form.setFieldValue(['changes', 0, 'inputChangeLeaseTerm'], maxTerm);
					}
				}
			}

			const leaseStartDate = form.getFieldValue('leaseStartDate');

			if (leaseStartDate && maxTerm) {
				const leaseEndDate = leaseStartDate.add(maxTerm, 'month').subtract(1, 'day');
				const currentLeaseEndDate = form.getFieldValue('leaseEndDate');

				// Only update if the value has actually changed
				if (!currentLeaseEndDate || !leaseEndDate.isSame(currentLeaseEndDate)) {
					form.setFieldValue('leaseEndDate', leaseEndDate);
				}
			}
		} finally {
			// Reset the flag after update is complete

			setTimeout(() => {
				isUpdatingRef.current = false;
			}, 0);
		}
	}, [grossRentList, advanceAmountList, screen]);

	const handlePaymentFrequencyChange = (value) => {
		if (isUpdatingRef.current) return value;

		isUpdatingRef.current = true;
		try {
			const currentChanges = form.getFieldValue('changes');
			if (screen.toLowerCase() === 'create') {
				form.setFieldValue(['changes', 0, 'paymentFrequency'], value);
			} else if (screen.toLowerCase() === 'revise') {
				form.setFieldValue(['changes', currentChanges.length - 1, 'paymentFrequency'], value);
			}
		} finally {
			isUpdatingRef.current = false;
		}
		return value;
	};

	const handlePaymentModalityChange = (value) => {
		if (isUpdatingRef.current) return value;

		isUpdatingRef.current = true;
		try {
			const currentChanges = form.getFieldValue('changes');
			if (screen.toLowerCase() === 'create') {
				form.setFieldValue(['changes', 0, 'paymentTime'], value);
			} else if (screen.toLowerCase() === 'revise') {
				form.setFieldValue(['changes', currentChanges.length - 1, 'paymentTime'], value);
			}
		} finally {
			isUpdatingRef.current = false;
		}
		return value;
	};

	const grossRentModalData = {
		head: 'Monthly Gross Rent Schedule',
		label: 'Define monthly gross rent values',
		action: 'update',
	};

	const advanceAmountModalData = {
		head: 'Advance Amount Schedule',
		label: 'Define advance amount and adjustment values',
		action: 'update',
	};

	return (
		<>
			<Row gutter={[16, 0]}>
				<Col xs={24}>
					<CardAccordion
						title="Lease Payment Schedule"
						customHeaderSX={{
							backgroundColor: '#e1f0fb !important',
							p: '10px',
							mb: 3,
						}}
						typographySX={{ fontSize: '16px !important' }}
						darkTitle={false}
						sx={{ mb: 2 }}
					>
						<GrossRent formListName="grossRentList" screen={screen} />
						<Form.Item name="grossRentListSet" noStyle />
						<Form.Item name="effectiveDateHistory" noStyle />
						<Form.Item name="identityId" noStyle />
						<Form.Item name="modificationSequence" noStyle />
					</CardAccordion>
				</Col>
				<Col xs={4}>
					<InputLabel label={constantFormValues?.paymentFrequency?.label} head={true} />
				</Col>
				<Col xs={8}>
					<Form.Item
						name={constantFormValues?.paymentFrequency?.key}
						rules={constantFormValues?.paymentFrequency?.rules}
						getValueFromEvent={handlePaymentFrequencyChange}
					>
						<Select
							disabled={screen === 'VIEW'}
							showSearch
							style={{ width: '100%', height: '32px', borderRadius: '4px' }}
							filterOption={true}
							placeholder="Select from the list"
							optionFilterProp="children"
						>
							{constantFormValues?.paymentFrequency?.options?.map(({ key, value, label }) => (
								<Select.Option key={key} value={value}>
									{label}
								</Select.Option>
							))}
						</Select>
					</Form.Item>
				</Col>
				<Col xs={4}>
					<InputLabel label={constantFormValues?.paymentModality?.label} head={true} />
				</Col>
				<Col xs={8}>
					<Form.Item
						name={constantFormValues?.paymentModality?.key}
						rules={constantFormValues?.paymentModality?.rules}
						getValueFromEvent={handlePaymentModalityChange}
					>
						<Select
							disabled={screen === 'VIEW'}
							showSearch
							style={{ width: '100%', height: '32px', borderRadius: '4px' }}
							filterOption={true}
							placeholder="Select from the list"
							optionFilterProp="children"
						>
							{constantFormValues?.paymentModality?.options?.map(({ key, value, label }) => (
								<Select.Option key={key} value={value}>
									{label}
								</Select.Option>
							))}
						</Select>
					</Form.Item>
				</Col>

				<Col xs={24}>
					<CardAccordion
						title="Advance Payment & Adjustment"
						customHeaderSX={{
							backgroundColor: '#e1f0fb !important',
							p: '10px',
							mb: 3,
						}}
						typographySX={{ fontSize: '16px !important' }}
						darkTitle={false}
						sx={{ mb: 2 }}
					>
						<AdvanceAmount formListName="advanceAmountList" screen={screen} dataFullyLoaded={dataFullyLoaded} />
						<Form.Item name="advanceAmountListSet" noStyle />
					</CardAccordion>
				</Col>
				<Col xs={24}>
					<CardAccordion
						title="Other Payments & Incentive"
						customHeaderSX={{
							backgroundColor: '#e1f0fb !important',
							p: '10px',
						}}
						typographySX={{ fontSize: '16px !important' }}
						darkTitle={false}
					>
						<Row gutter={[16, 0]}>
							<Col xs={4}>
								<InputLabel label={constantFormValues?.upfrontLeasePayment?.label} head={true} />
							</Col>
							<Col xs={8}>
								<Form.Item name={constantFormValues?.upfrontLeasePayment?.key} rules={constantFormValues?.upfrontLeasePayment?.rules}>
									<NumericFormat
										size="large"
										decimalScale={2}
										placeholder="0.00"
										fixedDecimalScale
										thousandSeparator=","
										isAllowed={withValueCap}
										style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
										customInput={Input}
										disabled={screen === 'VIEW'}
									/>
								</Form.Item>
							</Col>

							<Col xs={4}>
								<InputLabel label={constantFormValues?.initialDirectCost?.label} head={true} />
							</Col>
							<Col xs={8}>
								<Form.Item name={constantFormValues?.initialDirectCost?.key} rules={constantFormValues?.initialDirectCost?.rules}>
									<NumericFormat
										size="large"
										decimalScale={2}
										placeholder="0.00"
										fixedDecimalScale
										thousandSeparator=","
										isAllowed={withValueCap}
										style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
										customInput={Input}
										disabled={screen === 'VIEW'}
									/>
								</Form.Item>
							</Col>

							<Col xs={4}>
								<InputLabel label={constantFormValues?.securityDeposit?.label} head={true} />
							</Col>
							<Col xs={8}>
								<Form.Item name={constantFormValues?.securityDeposit?.key} rules={constantFormValues?.securityDeposit?.rules}>
									<NumericFormat
										size="large"
										decimalScale={2}
										placeholder="0.00"
										fixedDecimalScale
										thousandSeparator=","
										isAllowed={withValueCap}
										style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
										customInput={Input}
										disabled={screen === 'VIEW'}
									/>
								</Form.Item>
							</Col>
							<Col xs={4}>
								<InputLabel label={constantFormValues?.cashIncentiveAmount?.label} head={true} />
							</Col>
							<Col xs={8}>
								<Form.Item name={constantFormValues?.cashIncentiveAmount?.key} rules={constantFormValues?.securityDeposit?.rules}>
									<NumericFormat
										size="large"
										decimalScale={2}
										placeholder="0.00"
										fixedDecimalScale
										thousandSeparator=","
										isAllowed={withValueCap}
										style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
										customInput={Input}
										disabled={screen === 'VIEW'}
									/>
								</Form.Item>
							</Col>
						</Row>
					</CardAccordion>
				</Col>

				{/* Hidden form items to store the arrays for API call */}
				<Form.Item name={constantFormValues?.advanceAmount?.key} noStyle />
				<Form.Item name="advanceAmounts" noStyle />
				<Form.Item name="grossRents" noStyle />
				<Form.Item noStyle name={constantFormValues?.inputChangeMonth?.key} />
			</Row>

			{showGrossRentModal && screen !== 'VIEW' && (
				<TableContentModal
					form={form}
					showModal={showGrossRentModal}
					setShowModal={setShowGrossRentModal}
					formListName="grossRentSchedule"
					modalData={grossRentModalData}
					numberOfColumn={3}
					// onDataSave={() => setHasGrossRentData(true)}
				/>
			)}

			{showAdvanceAmountModal && screen !== 'VIEW' && (
				<TableContentModal
					form={form}
					showModal={showAdvanceAmountModal}
					setShowModal={setAdvanceAmountModal}
					formListName="advanceAmountSchedule"
					modalData={advanceAmountModalData}
					numberOfColumn={4}
					// onDataSave={() => setHasAdvanceAmountData(true)}
				/>
			)}
		</>
	);
};

export default FinancialTermsConditions;
